import { ref } from 'vue'
import { useEventListener } from '@vueuse/core'

// Cookiebot meldet jeden Stand über ein window-Event. `CookiebotOnConsentReady`
// kommt auch, wenn die Antwort schon im Cookie stand, die beiden anderen bei
// einer Entscheidung im Banner — auch beim Widerruf.
const EVENTS = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline']

/**
 * Zustimmung für eine Cookiebot-Kategorie als reaktiver Wert.
 *
 * Nötig, weil `data-blockingmode="auto"` hier nicht greift: Cookiebot blockt
 * <script src>-Tags und bekannte Trackerdomains im Markup. Was aus dem eigenen
 * Bundle heraus per fetch nachlädt — maplibre holt Style, Glyphen und Kacheln so
 * — läuft daran vorbei. Das Gate muss also in die Komponente.
 *
 * Nicht einmalig prüfen: Cookiebot lädt asynchron, beim Mount steht
 * `window.Cookiebot.consent` oft noch nicht.
 */
export function useConsent(category = 'preferences') {
    // Ohne COOKIEBOT_ID rendert _/includes/cookiebot.twig nichts — dann gibt es
    // keinen Consent-Manager, der zustimmen könnte, und die Karte bliebe lokal
    // für immer aus.
    const gated = !!document.getElementById('Cookiebot')
    const granted = ref(!gated || !!window.Cookiebot?.consent?.[category])

    const sync = () => {
        granted.value = !gated || !!window.Cookiebot?.consent?.[category]
    }

    if (gated) {
        EVENTS.forEach((name) => useEventListener(window, name, sync))
    }

    /**
     * Genau diese Kategorie freischalten — sonst nichts.
     *
     * `submitCustomConsent` erwartet alle drei Kategorien auf einmal. Die
     * übrigen gehen deshalb so wieder hinein, wie sie stehen: sonst nähme ein
     * Klick auf „Karte laden“ eine Zustimmung zu Statistik oder Marketing
     * zurück, die der Nutzer vorher im Banner gegeben hat.
     */
    const grant = () => {
        const cookiebot = window.Cookiebot

        if (!cookiebot) return

        // Ältere Fassungen kennen die Einzelzustimmung nicht. Dann bleibt das
        // Banner — lieber ein Umweg als ein toter Knopf.
        if (typeof cookiebot.submitCustomConsent !== 'function') {
            cookiebot.renew?.()
            return
        }

        const consent = cookiebot.consent ?? {}

        cookiebot.submitCustomConsent(
            category === 'preferences' || !!consent.preferences,
            category === 'statistics' || !!consent.statistics,
            category === 'marketing' || !!consent.marketing,
        )

        // Cookiebot meldet die Änderung über `CookiebotOnAccept`. Hier trotzdem
        // noch einmal nachlesen, damit der Knopf nicht am Event hängt.
        sync()
    }

    return { granted, gated, grant }
}
